import { createClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || import.meta.env.VITE_PAULIE_PUBLIC_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || import.meta.env.VITE_PAULIE_PUBLIC_SUPABASE_ANON_KEY
export const hasSupabaseEnv = Boolean(supabaseUrl && supabaseAnonKey)

if (!supabaseUrl || !supabaseAnonKey) {
  console.warn(
    'Missing Supabase environment variables. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.'
  )
}

const fallbackSupabaseUrl = 'https://invalid.public-supabase.local'
const fallbackSupabaseAnonKey = 'public-anon-key-missing'

export const supabase = createClient(supabaseUrl || fallbackSupabaseUrl, supabaseAnonKey || fallbackSupabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true
  }
})

// Auth helpers
export const auth = {
  // Sign up with email and password
  async signUp(email, password, userData = {}) {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: userData
      }
    })
    return { data, error }
  },

  // Sign in with email and password
  async signIn(email, password) {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    })
    return { data, error }
  },

  // Sign in with OAuth provider
  async signInWithOAuth(provider) {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: `${window.location.origin}/dashboard`
      }
    })
    return { data, error }
  },

  // Send magic link
  async sendMagicLink(email, options = {}) {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: options.redirectTo || `${window.location.origin}/dashboard`
      }
    })
    return { error }
  },

  // Send SMS code to an existing Supabase Auth user.
  async sendPhoneOtp(phone) {
    const { data, error } = await supabase.auth.signInWithOtp({
      phone,
      options: {
        shouldCreateUser: false
      }
    })
    return { data, error }
  },

  // Send SMS code for a new phone account and attach initial metadata.
  async sendSignupPhoneOtp(phone, userData = {}) {
    const { data, error } = await supabase.auth.signInWithOtp({
      phone,
      options: {
        shouldCreateUser: true,
        data: userData
      }
    })
    return { data, error }
  },

  // Verify SMS code for phone sign-in.
  async verifyPhoneOtp(phone, token) {
    const { data, error } = await supabase.auth.verifyOtp({
      phone,
      token,
      type: 'sms'
    })
    return { data, error }
  },

  // Update user metadata for the active authenticated user.
  async updateUserData(userData = {}) {
    const { data, error } = await supabase.auth.updateUser({
      data: userData
    })
    return { data, error }
  },

  // Sign out
  async signOut() {
    const { error } = await supabase.auth.signOut()
    return { error }
  },

  // Get current user
  async getUser() {
    const { data: { user }, error } = await supabase.auth.getUser()
    return { user, error }
  },

  // Get current session
  async getSession() {
    const { data: { session }, error } = await supabase.auth.getSession()
    return { session, error }
  },

  // Listen to auth changes
  onAuthStateChange(callback) {
    return supabase.auth.onAuthStateChange(callback)
  }
}

// Database helpers
export const db = {
  // Generic query helper
  async query(table, query = {}) {
    let supabaseQuery = supabase.from(table)
    
    if (query.select) supabaseQuery = supabaseQuery.select(query.select)
    if (query.where) {
      Object.entries(query.where).forEach(([column, value]) => {
        supabaseQuery = supabaseQuery.eq(column, value)
      })
    }
    if (query.orderBy) {
      supabaseQuery = supabaseQuery.order(query.orderBy.column, { ascending: query.orderBy.ascending })
    }
    if (query.limit) supabaseQuery = supabaseQuery.limit(query.limit)
    
    const { data, error } = await supabaseQuery
    return { data, error }
  },

  // Insert data
  async insert(table, data) {
    const { data: result, error } = await supabase
      .from(table)
      .insert(data)
      .select()
    return { data: result, error }
  },

  // Update data
  async update(table, data, where = {}) {
    let supabaseQuery = supabase.from(table).update(data)
    
    Object.entries(where).forEach(([column, value]) => {
      supabaseQuery = supabaseQuery.eq(column, value)
    })
    
    const { data: result, error } = await supabaseQuery.select()
    return { data: result, error }
  },

  // Delete data
  async delete(table, where = {}) {
    let supabaseQuery = supabase.from(table).delete()
    
    Object.entries(where).forEach(([column, value]) => {
      supabaseQuery = supabaseQuery.eq(column, value)
    })
    
    const { data, error } = await supabaseQuery
    return { data, error }
  }
}

export const userProfiles = {
  columns: 'id,username,phone_number,tier,is_subscribed,influencer_code',

  async fetch(userId) {
    const { data, error } = await supabase
      .from('users')
      .select(this.columns)
      .eq('id', userId)
      .maybeSingle()

    return { data, error }
  },

  async complete({ userId, phoneNumber, username, joinCode }) {
    if (!userId) {
      return { data: null, error: new Error('No authenticated user found.') }
    }

    const { data: existingProfile, error: fetchError } = await this.fetch(userId)

    if (fetchError) {
      return { data: null, error: fetchError }
    }

    const profileFields = {
      username,
      influencer_code: joinCode || null
    }

    if (existingProfile) {
      const { data, error } = await supabase
        .from('users')
        .update({
          ...profileFields,
          last_login: new Date().toISOString()
        })
        .eq('id', userId)
        .select(this.columns)
        .single()

      return { data, error }
    }

    const { data, error } = await supabase
      .from('users')
      .insert({
        id: userId,
        ...profileFields,
        phone_number: phoneNumber || '',
        tier: 'free',
        is_subscribed: false
      })
      .select(this.columns)
      .single()

    return { data, error }
  },

  async touchLogin(userId) {
    if (!userId) {
      return { data: null, error: new Error('No authenticated user found.') }
    }

    const { data, error } = await supabase
      .from('users')
      .update({ last_login: new Date().toISOString() })
      .eq('id', userId)
      .select(this.columns)
      .maybeSingle()

    return { data, error }
  }
}

export default supabase 
